import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Sparkles } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useServiceData } from '../../hooks/useServiceData'
import { useSubServiceData } from '../../hooks/useSubServiceData'
import carData from '../../data/car.json'
import './TableBlock.css'

const classIds = [1, 2, 3, 4, 5, 6]
const formatPrice = (price) =>
  price != null ? new Intl.NumberFormat('ru-RU').format(price) : '—'

const TableBlock = () => {
  const { slug, subSlug } = useParams()
  const dropdownRef = useRef(null)

  const { data: service } = useServiceData(slug)
  const { data: subService } = useSubServiceData(subSlug ? slug : null, subSlug)

  const brands = useMemo(
    () => [...new Set(carData.cars.map((car) => car.brand))].sort((a, b) => a.localeCompare(b, 'ru-RU')),
    []
  )

  const [selectedBrand, setSelectedBrand] = useState('')
  const [openDropdown, setOpenDropdown] = useState(null)
  const [mobileClassView, setMobileClassView] = useState(1)

  const models = useMemo(
    () =>
      [...new Set(carData.cars.filter((car) => car.brand === selectedBrand).map((car) => car.model))].sort(
        (a, b) => a.localeCompare(b, 'ru-RU')
      ),
    [selectedBrand]
  )

  const [selectedModel, setSelectedModel] = useState('')

  const selectedCarClass = useMemo(() => {
    const selectedCar = carData.cars.find(
      (car) => car.brand === selectedBrand && car.model === selectedModel
    )
    return selectedCar?.class || null
  }, [selectedBrand, selectedModel])

  const selectedClassLabel =
    selectedCarClass && carData.car_classes[String(selectedCarClass)]
      ? carData.car_classes[String(selectedCarClass)].label
      : ''

  useEffect(() => {
    if (selectedCarClass) setMobileClassView(selectedCarClass)
  }, [selectedCarClass])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleBrandSelect = (nextBrand) => {
    setSelectedBrand(nextBrand)
    setSelectedModel('')
    setOpenDropdown(null)
  }

  const handleModelSelect = (model) => {
    setSelectedModel(model)
    setOpenDropdown(null)
  }

  const toggleDropdown = (name) => {
    setOpenDropdown((prev) => (prev === name ? null : name))
  }

  // Строим rows из данных API в тот же формат, что раньше из servicePricingTree
  const rows = useMemo(() => {
    if (subSlug) {
      // Страница подуслуги: данные из subService.priceList
      if (!subService) return []
      const items = [...(subService.priceList || [])].sort((a, b) => a.order - b.order)
      return [
        { id: `${slug}-${subSlug}`, type: 'subservice', name: subService.title, prices: null },
        ...items.map((item, i) => ({
          id: `${slug}-${subSlug}-${i}`,
          type: 'subservice-item',
          name: item.name,
          prices: item.prices,
        })),
      ]
    }

    // Страница услуги: данные из service.priceList (агрегированный)
    if (!service) return []
    return (service.priceList || []).flatMap((group) => [
      {
        id: `${slug}-${group.subServiceSlug}`,
        type: 'subservice',
        name: group.subServiceTitle,
        prices: null,
      },
      ...group.items.map((item, i) => ({
        id: `${slug}-${group.subServiceSlug}-${i}`,
        type: 'subservice-item',
        name: item.name,
        prices: item.prices,
      })),
    ])
  }, [slug, subSlug, service, subService])

  const mobileRows = useMemo(() => {
    let currentGroup = ''
    return rows
      .map((row) => {
        if (!row.prices) {
          currentGroup = row.name
          return null
        }
        return { id: row.id, group: currentGroup || 'Услуга', name: row.name, prices: row.prices }
      })
      .filter(Boolean)
  }, [rows])

  return (
    <section className="table-block">
      <div className="table-block__inner">
        <div className="table-block__header">
          <p className="table-block__eyebrow">Прайс-лист</p>
          <h2 className="table-block__title">Определить класс вашего авто</h2>
        </div>

        <div className="table-block__controls" ref={dropdownRef}>
          <div className="table-block__field">
            <div className="table-block__custom-select">
              <button
                type="button"
                className={`table-block__select-button ${openDropdown === 'brand' ? 'is-open' : ''}`}
                onClick={() => toggleDropdown('brand')}
                aria-expanded={openDropdown === 'brand'}
              >
                <span>{selectedBrand || 'Марка'}</span>
                <span className="table-block__select-icon-box">
                  <ChevronDown size={16} className="table-block__select-icon" />
                </span>
              </button>
              {openDropdown === 'brand' && (
                <ul className="table-block__select-list" role="listbox">
                  {brands.map((brand) => (
                    <li key={brand}>
                      <button
                        type="button"
                        className={`table-block__select-option ${selectedBrand === brand ? 'is-active' : ''}`}
                        onClick={() => handleBrandSelect(brand)}
                      >
                        <span>{brand}</span>
                        {selectedBrand === brand ? <Check size={16} /> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="table-block__field">
            <div className="table-block__custom-select">
              <button
                type="button"
                className={`table-block__select-button ${openDropdown === 'model' ? 'is-open' : ''}`}
                onClick={() => { if (selectedBrand) toggleDropdown('model') }}
                aria-expanded={openDropdown === 'model'}
                disabled={!selectedBrand}
              >
                <span>{selectedModel || 'Модель'}</span>
                <span className="table-block__select-icon-box">
                  <ChevronDown size={16} className="table-block__select-icon" />
                </span>
              </button>
              {openDropdown === 'model' && (
                <ul className="table-block__select-list" role="listbox">
                  {models.map((model) => (
                    <li key={model}>
                      <button
                        type="button"
                        className={`table-block__select-option ${selectedModel === model ? 'is-active' : ''}`}
                        onClick={() => handleModelSelect(model)}
                      >
                        <span>{model}</span>
                        {selectedModel === model ? <Check size={16} /> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="table-block__selected-class">
            <span>
              <Sparkles size={15} />
              Определенный класс:
            </span>
            <strong>{selectedClassLabel}</strong>
          </div>
        </div>

        <div className="table-block__table-wrap table-block__table-wrap--desktop">
          <table className="table-block__table">
            <thead>
              <tr>
                <th>Прайс-лист на услуги регулярного ухода за автомобилем</th>
                {classIds.map((classId) => (
                  <th
                    key={classId}
                    className={selectedCarClass === classId ? 'is-selected-class' : undefined}
                  >
                    {carData.car_classes[String(classId)]?.label || `${classId} класс`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={classIds.length + 1}>Для текущей страницы прайс не найден.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className={`table-block__row-${row.type}`}>
                    {row.prices ? (
                      <>
                        <td>{row.name}</td>
                        {classIds.map((classId) => (
                          <td
                            key={`${row.id}-${classId}`}
                            className={selectedCarClass === classId ? 'is-selected-class' : undefined}
                          >
                            {formatPrice(row.prices[classId])}
                          </td>
                        ))}
                      </>
                    ) : (
                      <td colSpan={classIds.length + 1}>{row.name}</td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-block__mobile">
          <div className="table-block__mobile-classes">
            {classIds.map((classId) => (
              <button
                key={`mobile-class-${classId}`}
                type="button"
                className={`table-block__mobile-class-btn ${mobileClassView === classId ? 'is-active' : ''}`}
                onClick={() => setMobileClassView(classId)}
              >
                {classId} кл
              </button>
            ))}
          </div>

          <div className="table-block__mobile-list">
            {mobileRows.length === 0 ? (
              <div className="table-block__mobile-empty">Для текущей страницы прайс не найден.</div>
            ) : (
              mobileRows.map((row) => (
                <div key={`mobile-${row.id}`} className="table-block__mobile-card">
                  <div className="table-block__mobile-card-head">
                    <p className="table-block__mobile-group">{row.group}</p>
                  </div>
                  <div className="table-block__mobile-card-body">
                    <p className="table-block__mobile-name">{row.name}</p>
                    <p className="table-block__mobile-price">
                      {formatPrice(row.prices[mobileClassView])}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default TableBlock
